import React, { createContext, useContext, useState, FC, ReactNode } from 'react';
import { Colleague } from '../../services/service-supplier-signup';
import { CompanySearchRow } from '../../services/service-company-search';
import { logEvent } from '../../services/eventsService';

const SESSION_ID_KEY = 'supplier_signup_session_id';
const SOURCE_KEY = 'supplier_signup_source';
const PREFILL_KEY = 'supplier_signup_prefill';

/**
 * Generate an RFC4122 v4 UUID with graceful fallbacks.
 *
 * crypto.randomUUID() is only available in secure contexts (HTTPS/localhost)
 * and modern browsers. Some in-app browsers (LinkedIn, Facebook) and older
 * mobile browsers don't have it, which causes a runtime error. Fall back to
 * crypto.getRandomValues (widely supported), and Math.random as last resort.
 */
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }
  // Last-resort fallback (non-cryptographic) — used only when neither
  // crypto.randomUUID nor crypto.getRandomValues is available.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Returns the existing sessionId from sessionStorage, or generates and stores a new one.
 * Survives page refreshes within the same tab — clears when the tab is closed.
 * Falls back to an in-memory UUID if sessionStorage is unavailable (e.g. private browsing).
 */
export const getOrCreateSignupSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const id = generateUUID();
    sessionStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch {
    return generateUUID();
  }
};

const captureSignupSource = (): string | null => {
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get('source');
  } catch {
    // ignore — malformed/unavailable location
  }
  try {
    if (fromUrl) {
      sessionStorage.setItem(SOURCE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(SOURCE_KEY);
  } catch {
    return fromUrl;
  }
};

type SignupPrefill = Partial<Pick<SignupWizardState,
  'organizationNumber' | 'name' | 'email' | 'title' | 'phoneNumber' | 'addAsContact'>>;

/**
 * Values an entry point can hand the wizard through the URL — e.g. the "create
 * account" banner in a bid request, which already knows the supplier's org
 * number and the contact it sent the request to. Persisted like the source so
 * a refresh mid-wizard doesn't lose them.
 */
const capturePrefill = (): SignupPrefill => {
  let fromUrl: SignupPrefill | null = null;
  try {
    const query = new URLSearchParams(window.location.search);
    const entries: SignupPrefill = {};
    (['organizationNumber', 'name', 'email', 'title', 'phoneNumber'] as const).forEach(key => {
      const value = query.get(key);
      if (value) entries[key] = value;
    });
    const addAsContact = query.get('addAsContact');
    if (addAsContact) entries.addAsContact = addAsContact === 'true';
    if (Object.keys(entries).length > 0) fromUrl = entries;
  } catch {
    // ignore — malformed/unavailable location
  }
  try {
    if (fromUrl) {
      sessionStorage.setItem(PREFILL_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = sessionStorage.getItem(PREFILL_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return fromUrl ?? {};
  }
};

/** Call after successful account creation to allow a fresh session next time. */
export const clearSignupSession = (): void => {
  try {
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SOURCE_KEY);
    sessionStorage.removeItem(PREFILL_KEY);
  } catch {
    // ignore
  }
};

interface SignupWizardState {
  organizationNumber: string;
  companyPick: CompanySearchRow | null;
  companyName: string;
  address: string;
  city: string;
  postalCode: string;
  termsAcceptedAt: string | null;
  areaIds: string[];
  places: string[];
  name: string;
  title: string;
  phoneNumber: string;
  email: string;
  password: string;
  emailExists: boolean | null; //Result of step 4 email check, to be used in step 6
  addAsContact: boolean;
  colleagues: Colleague[];
  currentStep: number;
  completedSteps: number[];
  sessionId: string;
  source: string | null;
}

interface SignupWizardContextType {
  state: SignupWizardState;
  updateState: (partial: Partial<SignupWizardState>) => void;
  completeStep: (step: number) => void;
  setCurrentStep: (step: number) => void;
  isStepCompleted: (step: number) => boolean;
  trackEvent: (event: string) => void;
  withSource: (path: string) => string;
}

const createInitialState = (): SignupWizardState => ({
  organizationNumber: '',
  companyPick: null,
  companyName: '',
  address: '',
  city: '',
  postalCode: '',
  termsAcceptedAt: null,
  areaIds: [],
  places: [],
  name: '',
  title: '',
  phoneNumber: '',
  email: '',
  password: '',
  emailExists: null,
  addAsContact: true,
  colleagues: [],
  currentStep: 1,
  completedSteps: [],
  sessionId: getOrCreateSignupSessionId(),
  source: captureSignupSource(),
  ...capturePrefill(),
});

const SignupWizardContext = createContext<SignupWizardContextType | null>(null);

export const SignupWizardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SignupWizardState>(createInitialState);

  const updateState = (partial: Partial<SignupWizardState>) => {
    setState(prev => ({ ...prev, ...partial }));
  };

  const completeStep = (step: number) => {
    setState(prev => ({
      ...prev,
      completedSteps: prev.completedSteps.includes(step)
        ? prev.completedSteps
        : [...prev.completedSteps, step],
    }));
  };

  const setCurrentStep = (step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
  };

  const isStepCompleted = (step: number) => state.completedSteps.includes(step);

  const trackEvent = (event: string) => {
    const metadata: { sessionId: string; source?: string } = { sessionId: state.sessionId };
    if (state.source) metadata.source = state.source;
    logEvent({ event, metadata, userIdentifier: state.sessionId, options: { unique: true } });
  };

  const withSource = (path: string): string => {
    if (!state.source) return path;
    const [base, query] = path.split('?');
    const params = new URLSearchParams(query);
    params.set('source', state.source);
    return `${base}?${params.toString()}`;
  };

  return (
    <SignupWizardContext.Provider value={{ state, updateState, completeStep, setCurrentStep, isStepCompleted, trackEvent, withSource }}>
      {children}
    </SignupWizardContext.Provider>
  );
};

export const useSignupWizard = (): SignupWizardContextType => {
  const context = useContext(SignupWizardContext);
  if (!context) throw new Error('useSignupWizard must be used within SignupWizardProvider');
  return context;
};
